import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import styles from './HolographicHeadline.module.scss';

const HolographicHeadline = ({
  primaryText = 'Brand your emails.',
  animatedLines = ['Get more opens.', 'Gain instant trust.', 'Get a blue checkmark.'],
  animatedWords = null,
  staticText = 'Get more ',
  interval = 3000,
  className = '',
}) => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Determine if we're animating full lines or single words
  const isWordMode = !!animatedWords;
  const items = isWordMode ? animatedWords : animatedLines;

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % items.length);
    }, interval);

    return () => clearInterval(timer);
  }, [isPaused, items.length, interval]);

  return (
    <h1
      className={`${styles.headline} ${className} ${isWordMode ? styles.wordMode : ''}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={styles.headlineContainer}>
        <motion.div
          className={styles.primaryText}
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          {primaryText}
        </motion.div>

        {isWordMode ? (
          <motion.div
            className={`${styles.secondaryWrapper} ${styles.textNoWrap}`}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.secondaryContainer}>
              <div className={styles.staticText}>{staticText}</div>
              <div className={styles.rotatingTextContainer}>
                <div className={styles.rotatingText} aria-live="polite">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current}
                      initial={{ opacity: 0, filter: 'blur(5px)', y: 10 }}
                      animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                      exit={{ opacity: 0, filter: 'blur(5px)', y: -10 }}
                      transition={{ duration: 0.5 }}
                      className={styles.holographic}
                    >
                      {items[current]}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className={`${styles.rotatingLineContainer} ${styles.textNoWrap}`}>
            <div className={styles.rotatingText} aria-live="polite">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, filter: 'blur(5px)', scale: 0.9, x: 100 }}
                  animate={{ opacity: 1, filter: 'blur(0px)', scale: 1, x: 0 }}
                  exit={{ opacity: 0, filter: 'blur(5px)', scale: 0.9, x: -100 }}
                  transition={{ duration: 0.8 }}
                  className={styles.holographic}
                >
                  {items[current]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </h1>
  );
};

export default HolographicHeadline;
